import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import {
  AppSettings,
  DEFAULT_ISSUE_TYPES,
  DEFAULT_ACCESSORIES,
  DEFAULT_DEVICE_CATEGORIES,
  DEFAULT_DEVICE_BRANDS,
  DEFAULT_VAT_RATE_PERCENT,
} from '../types/typesSettings';

const SETTINGS_DOC_PATH = 'settings/app';

type SettingsContextValue = {
  settings: AppSettings;
  /** רשימת סוגי תקלה לשימוש בטופס (ממוינת) */
  issueTypesOptions: string[];
  /** רשימת ציוד נלווה לשימוש בטופס */
  accessoriesOptions: string[];
  /** רשימת קטגוריות מכשיר לבחירה בטפסים */
  deviceCategoriesOptions: string[];
  /** רשימת מותגים לבחירה בטפסים */
  deviceBrandsOptions: string[];
  /** אחוז מע"מ (0–100) */
  vatRatePercent: number;
  /** מספר תיק מע"מ להדפסה */
  vatNumber: string;
  loading: boolean;
  error: string | null;
  /** עדכון הגדרות – למנהל בלבד */
  updateSettings: (partial: Partial<AppSettings>) => Promise<void>;
};

const defaultSettings: AppSettings = {
  vatRatePercent: DEFAULT_VAT_RATE_PERCENT,
  vatNumber: process.env.REACT_APP_INVOICE_VAT || 'ח.פ. / ע.מ.',
  issueTypes: DEFAULT_ISSUE_TYPES,
  accessories: DEFAULT_ACCESSORIES,
  deviceCategories: DEFAULT_DEVICE_CATEGORIES,
  deviceBrands: DEFAULT_DEVICE_BRANDS,
};

const SettingsContext = createContext<SettingsContextValue | null>(null);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ref = doc(db, SETTINGS_DOC_PATH);
    const apply = (data: Record<string, unknown> | undefined) => {
      if (!data) return;
      setSettings({
        vatRatePercent: typeof data.vatRatePercent === 'number' ? data.vatRatePercent : defaultSettings.vatRatePercent,
        vatNumber: typeof data.vatNumber === 'string' ? data.vatNumber : defaultSettings.vatNumber,
        issueTypes: Array.isArray(data.issueTypes) ? data.issueTypes : defaultSettings.issueTypes,
        accessories: Array.isArray(data.accessories) ? data.accessories : defaultSettings.accessories,
        deviceCategories: Array.isArray(data.deviceCategories) ? data.deviceCategories : defaultSettings.deviceCategories,
        deviceBrands: Array.isArray(data.deviceBrands) ? data.deviceBrands : defaultSettings.deviceBrands,
        updatedAt: data.updatedAt as string | undefined,
      });
      setError(null);
    };
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        setLoading(false);
        if (snap.exists()) apply(snap.data() as Record<string, unknown>);
      },
      (err) => {
        setLoading(false);
        const code = String((err as any)?.code || '');
        if (code.includes('permission-denied')) {
          // במסך התחברות המשתמש עדיין לא מאומת; לא נציג שגיאה "רועשת" על ההגדרות.
          setError(null);
          return;
        }
        console.error('[SettingsContext] load failed', err);
        setError(err?.message || 'שגיאה בטעינת הגדרות');
      }
    );
    return () => unsubscribe();
  }, []);

  const updateSettings = useCallback(async (partial: Partial<AppSettings>) => {
    const ref = doc(db, SETTINGS_DOC_PATH);
    const next = {
      ...settings,
      ...partial,
      updatedAt: new Date().toISOString(),
    };
    await setDoc(ref, next, { merge: true });
    setSettings(next);
  }, [settings]);

  // רשימת סוגי תקלה בפועל – מאחדת בין מה שמוגדר בהגדרות לבוררת המחדל,
  // כדי שתמיד תכלול את כל סוגי התקלות החדשים גם אם במסד יש רק חלק מהם.
  const issueTypesOptions = Array.from(
    new Set([...(Array.isArray(settings.issueTypes) ? settings.issueTypes : []), ...DEFAULT_ISSUE_TYPES])
  ).sort((a, b) => a.localeCompare(b, 'he'));
  const accessoriesOptions = Array.isArray(settings.accessories) && settings.accessories.length > 0
    ? settings.accessories
    : DEFAULT_ACCESSORIES;
  const deviceCategoriesOptions = Array.isArray(settings.deviceCategories) && settings.deviceCategories.length > 0
    ? settings.deviceCategories
    : DEFAULT_DEVICE_CATEGORIES;
  const deviceBrandsOptions = Array.isArray(settings.deviceBrands) && settings.deviceBrands.length > 0
    ? settings.deviceBrands
    : DEFAULT_DEVICE_BRANDS;
  const vatRatePercent = typeof settings.vatRatePercent === 'number' ? settings.vatRatePercent : DEFAULT_VAT_RATE_PERCENT;
  const vatNumber = typeof settings.vatNumber === 'string' && settings.vatNumber
    ? settings.vatNumber
    : (process.env.REACT_APP_INVOICE_VAT || 'ח.פ. / ע.מ.');

  const value: SettingsContextValue = {
    settings,
    issueTypesOptions,
    accessoriesOptions,
    deviceCategoriesOptions,
    deviceBrandsOptions,
    vatRatePercent,
    vatNumber,
    loading,
    error,
    updateSettings,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    return {
      settings: defaultSettings,
      issueTypesOptions: DEFAULT_ISSUE_TYPES,
      accessoriesOptions: DEFAULT_ACCESSORIES,
      deviceCategoriesOptions: DEFAULT_DEVICE_CATEGORIES,
      deviceBrandsOptions: DEFAULT_DEVICE_BRANDS,
      vatRatePercent: DEFAULT_VAT_RATE_PERCENT,
      vatNumber: process.env.REACT_APP_INVOICE_VAT || 'ח.פ. / ע.מ.',
      loading: false,
      error: null,
      updateSettings: async () => {},
    };
  }
  return ctx;
}
